import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { useSelector } from 'react-redux';
import { RiArrowDownSFill, RiArrowUpSFill } from 'react-icons/ri';

import { MobileSm, DefaultSm } from 'components/MediaQueries';
import { FULL_LEADS_HEADERS } from 'utils/constants';
import { getLocalDateString } from 'utils/general';
import { useStickyHeader } from 'hooks/useStickyHeader';

import { LeadContactCell } from './Leads.ContactCell';
import { CountControl } from './Leads.CountControl';
import { LeadsStatusDropdown } from './Leads.StatusDropdown';
import { LastContactedDropdown } from './Leads.LastContactedDropdown';
import { LeadsOptionsButton } from './Leads.OptionsButton';
import { AssignmentDropdown } from './Leads.AssignmentDropdown';

dayjs.extend(relativeTime);

const ITEMS_PER_PAGE = [10, 25, 50, 100];
export const SORTABLE_COLUMNS = [
  {
    id: 'contact_first_name',
    label: 'Lead Contact',
  },
  {
    id: 'wedding_date',
    label: 'Event Date/Location',
  },
  {
    id: 'wedding_date',
    label: 'Event Date',
  },
  {
    id: 'date_inquiry',
    label: 'Inquiry Date',
  },
  {
    id: 'status',
    label: 'Status',
  },
  {
    id: 'last_contacted_date',
    label: 'Last Contacted',
  },
  {
    id: 'project_name',
    label: 'Project Name',
  },
  {
    id: 'service_type',
    label: 'Service Type',
  },
  {
    id: 'budget',
    label: 'Budget',
  },
  {
    id: 'lead_source',
    label: 'Lead Source',
  },
];
const COMBINED_COLUMNS = [
  'contactName',
  'contactEmail',
  'contactNumber',
  'contactCompanyName',
  'eventDate',
  'eventLocation',
];
export const SORT = { ASC: 1, DESC: 0, NONE: -1 };

const LeadsTable = ({
  leads,
  lostReasons,
  selected,
  totalCount,
  pageNum,
  changePage,
  pageSize,
  changePageSize,
  sortColumn,
  changeSortColumn,
}) => {
  const filters = useSelector(state => state.leads.filters);
  const leadViewOptions = useSelector((state) => state.global.leadsViewOptions);
  const lostStatus = filters.find(status => status.name === 'Lost');
  const selectedName = filters?.filter(fl => String(fl.id) === String(selected))[0]?.name;
  const sortColumnReverse = sortColumn.sortReverse;

  const userInfo = useSelector((state) => state.global.userInfo);
  const organization = useSelector((state) => state.global.userInfo.organization);
  const { orgId } = userInfo;
  const { datePreference } = organization;
  const dateFormat = datePreference?.toUpperCase();

  const visibleColumns = leadViewOptions.filter(option => option.checked).map(item => item.id);

  const { tableRef, isSticky } = useStickyHeader(false, 180, 140);
  const stickyRef = useRef(null);

  useEffect(() => {
    // Scroll the sticky header if table is scrolled.
    const handleScroll = () => {
      if (stickyRef.current && tableRef.current) {
        stickyRef.current.parentNode.scrollLeft = tableRef.current.parentNode.scrollLeft;
      }
    };

    // Manually set width of sticky header cells from leads table cells.
    const headers = document.querySelectorAll('.sticky-table th');

    if (stickyRef.current && tableRef.current) {
      tableRef.current.parentNode?.addEventListener('scroll', handleScroll);
      stickyRef.current.style.width = `${tableRef.current.offsetWidth}px`;
      const stickyHeaders = document.querySelectorAll('.sticky-table--header th');
      stickyHeaders.forEach((th, index) => {
        th.style.width = `${headers[index].offsetWidth}px`;
      });
      stickyRef.current.parentNode.scrollLeft = tableRef.current.parentNode.scrollLeft;
    }

    return () => tableRef.current?.parentNode.removeEventListener('scroll', handleScroll);
  }, [isSticky, stickyRef, leads]);

  const handlePageChange = (page) => {
    changePage(page);
  };

  const onRowCountChange = (count) => {
    changePage(0);
    changePageSize(count);
  };

  const isInOptionMenu = (element) => {
    if (!element.parentElement) return false;
    if (element.classList.contains('ap-dropdown__content') ||
      element.classList.contains('ReactModal__Overlay') ||
      element.classList.contains('drawer-container') ||
      element.classList.contains('lead-record__options') ||
      element.classList.contains('ap-responsive-dropdown') ||
      element.classList.contains('ap-dropdown__trigger')) {
        return true;
    }
    return isInOptionMenu(element.parentElement);
  };

  const goToLead = (event, leadId) => {
    if (!isInOptionMenu(event.target, leadId)) {
      window.location = `/app/pro/${orgId}/leads/lead/${leadId}`;
    }
  };

  const handleHeaderColumnClick = (item) => {
    const found = SORTABLE_COLUMNS.findIndex(el => el.label === item);
    if (found < 0) return;
    const existingSort = sortColumn.name === item ? sortColumnReverse : 0;

    changeSortColumn({
      name: item,
      sortReverse: existingSort === 1 ? 0 : 1,
    });
  };

  const renderTableHeader = () => {
    const headers = ['Lead Contact'];
    let eventColumn = '';
    if (visibleColumns.includes('eventDate') && visibleColumns.includes('eventLocation'))
      eventColumn = 'Event Date/Location';
    else if (visibleColumns.includes('eventDate'))
      eventColumn = 'Event Date';
    else if (visibleColumns.includes('eventLocation'))
      eventColumn = 'Location';

    visibleColumns.forEach(el => {
      if (el === 'lostReasonId' && selectedName !== 'Lost') return;
      if (el === 'eventDate' || el === 'eventLocation') {
        if (headers.includes('Event Date/Location')) return;
        if (headers.includes('Event Date')) return;
        if (headers.includes('Location')) return;
        headers.push(eventColumn);
      } else if (!COMBINED_COLUMNS.includes(el)) {
        headers.push(FULL_LEADS_HEADERS[el]);
      }
    });

    return (
      <>
        {headers.map((item, index) => {
          const found = SORTABLE_COLUMNS.findIndex(el => el.label === item);
          const sorted = (found >= 0) && (item === sortColumn.name) ? sortColumnReverse : -1;

          return (
            <th
              className="table-item_head"
              key={`table-item_head--${index}`}
              onClick={() => handleHeaderColumnClick(item)}
            >
              <div className="table-item_head--container">
                <span>{item}</span>
                <span className="table-item_head--symbol">
                  {sorted === SORT.ASC ? (
                    <RiArrowUpSFill size={16} />
                  ) : sorted === SORT.DESC ? (
                    <RiArrowDownSFill size={16} />
                  ) :(
                    ''
                  )}
                </span>
              </div>
            </th>
          );
        })}
        <th className='table-item_head' />
      </>
    );
  };

  // #TODO: Refactor renderTableRows so code/logic doesn't need to be duplicated
  //        for renderMobileRows.
  const renderTableRows = rowData => {
    const { leadItem, index } = rowData;
    const contact = {
      contactEmail: leadItem.contactEmail,
      contactNumber: leadItem.contactNumber,
      contactFirstName: leadItem.contactFirstName,
      contactLastName: leadItem.contactFirstName,
    };
    const rows = [
      <LeadContactCell
        key={`contact-cell-${leadItem.id}`}
        leadItem={leadItem}
        visibleColumns={visibleColumns}
      />,
    ];
    let hasEventField = false;
    const isPassed = leadItem.eventDate1 ?
      dayjs().isAfter(dayjs(leadItem.eventDate1).format('MM/DD/YYYY')) : false;
    visibleColumns.forEach((el, idx) => {
      if (el === 'lostReasonId' && selectedName !== 'Lost') return;
      if ((el === 'eventDate' || el === 'eventLocation') && !hasEventField) {
        const eventCell = (
          <td
            key={`table-item-${idx}`}
            className="table-item"
          >
            <div className="lead-record__date-location">
              {!leadItem.eventDate ? 'TBD' : null}
              {visibleColumns.includes('eventDate') && leadItem.eventDate && (
                <div className="lead-record__date">{leadItem.eventDate}</div>
              )}
              {visibleColumns.includes('eventLocation') && (
                <>
                  {leadItem.venueName ? (
                    <div className="lead-record__venue">{leadItem.venueName}</div>
                  )
                  : (leadItem.eventDate && leadItem.eventLocation) &&
                  (
                    <div className="lead-record__venue">TBD</div>
                  )}
                  {leadItem.eventLocation ? (
                    <div className="lead-record__location">{leadItem.eventLocation}</div>
                  )
                  // #TODO: This logic could be cleaned up but I didn't have the thought
                  // power to handle it in APP-7344.
                  : (
                    (leadItem.eventDate && leadItem.venueName) ||
                    leadItem.eventDate ||
                    (!leadItem.eventDate && leadItem.venueName)) &&
                  (
                    <div className="lead-record__location">TBD</div>
                  )
                }
                  </>
              )}
            </div>
          </td>
        );
        hasEventField = true;
        rows.push(eventCell);
      } else if (!COMBINED_COLUMNS.includes(el)) {
        let cell = leadItem[el];
        if (el === 'status') {
          cell = (
            <LeadsStatusDropdown
              leadId={leadItem.id}
              lostReasons={lostReasons}
              statuses={filters}
              active={leadItem.leadCustomStatusId}
            />
          );
        }
        else if (el === 'leadAssignments') {
          cell = (
            <AssignmentDropdown
              leadId={leadItem.id}
              assignments={leadItem.organizationMemberAssignments}
            />
          );
        }
        else if (el === 'lastContactedDate') {
          cell = (
            <LastContactedDropdown
              leadId={leadItem.id}
              date={leadItem.lastContactedDate}
              standardDate={leadItem.lastContactedDate1}
            />
          );
        }
        else if (el === 'estimateBudget' && leadItem.estimateBudget) {
          cell = leadItem.estimateBudgetFormatted;
        }
        else if (el === 'lostReasonId') {
          cell = lostReasons.find(reason => leadItem[el] === reason.id)?.name ?? '';
        }
        else if (el === 'dateInquiry' && leadItem.dateInquiry && leadItem.createdAt) {
          const tooltipTitle = getLocalDateString(leadItem.dateInquiry);
          const dtInq = dayjs(leadItem.dateInquiry).from(dayjs().format('YYYY-MM-DD'));
          if (dayjs().isSame(leadItem.dateInquiry, 'day')) {
            cell = (
              <a title={tooltipTitle}>Today</a>
            );
          } else {
            cell = (
              <a title={tooltipTitle}>{dtInq}</a>
            );
          }

        }
        rows.push(
          <td
            key={`table-item-${idx}`}
            className="table-item"
          >
            {cell || ''}
          </td>,
        );
      }
    });

    return (
      <tr
        key={`lead-record-${index}`}
        className={clsx('lead-record', {
          'is-passed': isPassed,
        })}
        onClick={(e) => goToLead(e, leadItem.id)}
      >
        {rows}
        <td className="table-item">
          <div className="lead-record__options">
            <div className={clsx('lead-record__options-inner', 'mx-auto')}>
              <LeadsOptionsButton
                leadId={leadItem.id}
                orgId={orgId}
                lostReasons={lostReasons}
                lostStatus={lostStatus}
                active={leadItem.leadCustomStatusId}
                contact={contact}
              />
            </div>
          </div>
        </td>
      </tr>
    );
  };

  const renderMobileRows = rowData => {
    const { leadItem, index } = rowData;
    const contact = { contactEmail: leadItem.contactEmail, contactNumber: leadItem.contactNumber };
    const rows = [];
    let hasEventField = false;
    const isPassed = leadItem.eventDate ?
      dayjs().isAfter(dayjs(leadItem.eventDate).format('MM/DD/YYYY')) : false;
    visibleColumns.forEach((el, idx) => {
      if (el === 'lostReasonId' && selectedName !== 'Lost') return;
      if ((el === 'eventDate' || el === 'eventLocation') && !hasEventField) {
        const eventCell = (
          <div
            key={`lead-record__data--${idx}`}
            className="lead-record__data"
          >
            {(leadItem.eventDate || leadItem.eventLocation) && (
              <>
                <div className="lead-record__heading">
                  {visibleColumns.includes('eventDate') && 'Date'}
                  {visibleColumns.includes('eventDate') && visibleColumns.includes('eventLocation') && '/'}
                  {visibleColumns.includes('eventLocation') && 'Location'}
                </div>
                <div className="lead-record__date-location">
                  {visibleColumns.includes('eventDate') && (
                    <div className="lead-record__date">{leadItem.eventDate}</div>
                  )}
                  {visibleColumns.includes('eventLocation') && leadItem.venueName && (
                    <div className="lead-record__venue">{leadItem.venueName}</div>
                  )}
                  {visibleColumns.includes('eventLocation') && (
                    <div className="lead-record__location">{leadItem.eventLocation}</div>
                  )}
                </div>
              </>
            )}
          </div>
        );
        hasEventField = true;
        rows.push(eventCell);
      } else if (!COMBINED_COLUMNS.includes(el)) {
        let cell;

        if (el === 'status') {
          cell = (
            <>
              <div className="lead-record__heading">
                {FULL_LEADS_HEADERS[el]}
              </div>
              <LeadsStatusDropdown
                leadId={leadItem.id}
                lostReasons={lostReasons}
                statuses={filters}
                active={leadItem.leadCustomStatusId}
              />
            </>
          );
        }
        else if (el === 'leadAssignments') {
          cell = (
            <>
              <div className="lead-record__heading">
                {FULL_LEADS_HEADERS[el]}
              </div>
              <AssignmentDropdown
                leadId={leadItem.id}
                assignments={leadItem.organizationMemberAssignments}
              />
            </>
          );
        }
        else if (el === 'lastContactedDate') {
          cell = (
            <>
              <div className="lead-record__heading">
                {FULL_LEADS_HEADERS[el]}
              </div>
              <LastContactedDropdown
                leadId={leadItem.id}
                date={leadItem.lastContactedDate}
                dateFormat={dateFormat}
              />
            </>
          );
        }
        else if (el === 'estimateBudget' && leadItem.estimateBudget) {
          cell = leadItem.estimateBudgetFormatted;
        }
        else if (el === 'lostReasonId') {
          cell = lostReasons.find(reason => leadItem[el] === reason.id)?.name ?? '';
        }
        else if (el === 'dateInquiry' && leadItem.dateInquiry && leadItem.createdAt) {
          const tooltipTitle = getLocalDateString(leadItem.dateInquiry);
          const dtInq = dayjs(leadItem.dateInquiry).fromNow();
          if (dayjs().isSame(leadItem.dateInquiry, 'day')) {
            cell = (
              <a title={tooltipTitle}>Today</a>
            );
          } else {
            cell = (
              <a title={tooltipTitle}>{dtInq}</a>
            );
          }

        }
        else if (leadItem[el] && leadItem[el] !== '') {
          cell = leadItem[el];
        }
        rows.push(
          <div
            key={`lead-record__data--${idx}`}
            className="lead-record__data"
          >
            {leadItem[el] && leadItem[el] !== '' && el !== 'status' && el !== 'lastContactedDate' && (
              <div className="lead-record__heading">
                {FULL_LEADS_HEADERS[el]}
              </div>
            )}
            {cell || ''}
          </div>,
        );
      }
    });

    return (
      <div
        key={`lead-record-mobile-${index}`}
        className={clsx('lead-record', 'lead-record--card', {
          'is-passed': isPassed,
        })}
        onClick={(e) => goToLead(e, leadItem.id)}
      >
        <div className="lead-record__header">
          <div className="lead-record__data lead-record__data--contact">
            <LeadContactCell
              leadItem={leadItem}
              visibleColumns={visibleColumns}
            />
          </div>
          <div className="lead-record__options">
            <LeadsOptionsButton
              leadId={leadItem.id}
              orgId={orgId}
              lostReasons={lostReasons}
              lostStatus={lostStatus}
              active={leadItem.leadCustomStatusId}
              contact={contact}
            />
          </div>
        </div>
        {rows}
      </div>
    );
  };

  return (
    <div key="leads-table-container">
      <div className="table-container table-container--leads">
        <MobileSm>
          <div className="lead-record-table">
            {leads?.map((leadItem, index) => renderMobileRows({ leadItem, index }))}
          </div>
        </MobileSm>
        <DefaultSm>
          <div
            className={clsx('sticky-table__wrapper', isSticky && 'show')}
            style={{
              position: 'fixed',
              top: 176,
              left: '50%',
              right: 0,
              transform: 'translateX(-50%)',
            }}
          >
            <table
              ref={stickyRef}
              className="sticky-table--header"
            >
              <thead>
                <tr>
                  {renderTableHeader()}
                </tr>
              </thead>
            </table>
          </div>
          <table
            ref={tableRef}
            className="sticky-table w-100 mt-3"
          >
            <thead>
              <tr>{renderTableHeader()}</tr>
            </thead>
            <tbody>
              {leads?.map((leadItem, index) => renderTableRows({ leadItem, index }))}
            </tbody>
          </table>
        </DefaultSm>
      </div>
      <div className="pager mt-3">
        <div className="pager__information">
          <span>
            Viewing {pageNum * pageSize + 1} -
            {pageNum * pageSize + pageSize < totalCount ?
              pageNum * pageSize + pageSize : totalCount}&nbsp;
            of {totalCount} results
          </span>
        </div>
        <DefaultSm>
          <CountControl
            options={ITEMS_PER_PAGE}
            onChange={onRowCountChange}
            selected={pageSize}
          />
        </DefaultSm>
        <div className="pager__buttons">
          <button
            onClick={() => handlePageChange(pageNum - 1)}
            disabled={pageNum === 0}
            className="button--unstyled text--unstyled pager__button pager__button--prev"
          >
            Prev
          </button>
          |
          <button
            onClick={() => handlePageChange(pageNum + 1)}
            disabled={(pageNum + 1) * pageSize >= totalCount}
            className="button--unstyled pager__button pager__button--next"
          >
            Next
          </button>
        </div>
      </div>
      <MobileSm>
        <CountControl
          options={ITEMS_PER_PAGE}
          onChange={onRowCountChange}
          selected={pageSize}
        />
      </MobileSm>
    </div>
  );
};

LeadsTable.propTypes = {
  leads: PropTypes.arrayOf(PropTypes.object),
  selected: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]),
  totalCount: PropTypes.number,
  pageNum: PropTypes.number,
  changePage: PropTypes.func,
  pageSize: PropTypes.number,
  changePageSize: PropTypes.func,
  sortColumn: PropTypes.object,
  changeSortColumn: PropTypes.func,
};

LeadsTable.defaultTypes = {
  leads: [],
  selected: 'current',
  totalCount: 0,
  pageNum: 0,
  changePage: () => {},
  pageSize: 10,
  changePageSize: () => {},
  sortColumn: {},
  changeSortColumn: () => {},
};

export default LeadsTable;
